import { useEffect, useMemo, useState } from 'react'

import { CoordinateFragment } from '../../../types/graphql'
import ArcGisMap from '../../components/shared/ArcGisMap'
import { MapShape, PopupTemplateAction } from '../../components/shared/ArcGisMap/types'

export type MapShapeAction<T extends { id: number }> = Omit<PopupTemplateAction, 'onClick'> & {
  onShapeActionClick: (datum: T) => void
}

type Props<T extends { id: number }> = {
  data: T[]
  getAddress: (datum: T) => string | null | undefined
  getCoordinates: (datum: T) => CoordinateFragment[] | null | undefined
  mapShapeActions?: MapShapeAction<T>[]
  getTitle: (datum: T) => string
  isLoading?: boolean
}

export function DataMap<T extends { id: number }>({
  data,
  getAddress,
  getCoordinates,
  mapShapeActions,
  getTitle,
  isLoading,
}: Props<T>) {
  const shapes: MapShape[] = useMemo(
    () =>
      data?.flatMap((datum) => {
        const coordinates = getCoordinates(datum)
        if (!coordinates?.length) {
          return []
        }

        const actions = mapShapeActions?.map((action) => ({
          icon: action.icon,
          key: action.key,
          onClick: () => action.onShapeActionClick(datum),
          title: action.title,
        }))

        return {
          description: getAddress(datum) ?? undefined,
          actions,
          shape: {
            coordinates,
            type: 'coordinates',
          },
          shapeKey: `shape-${datum.id}`,
          showPin: true,
          title: getTitle(datum),
        }
      }) ?? [],
    [data, getAddress, getCoordinates, getTitle, mapShapeActions]
  )
  const [shapesToDisplay, setShapesToDisplay] = useState(shapes)
  useEffect(() => {
    if (isLoading) {
      return // Show the previously loaded map until the new query is complete, otherwise it's a long period of showing a blank map
    }
    setShapesToDisplay(shapes)
  }, [isLoading, shapes])

  if (!shapesToDisplay?.length) {
    return null
  }

  return <ArcGisMap shapes={shapesToDisplay} isLoading={isLoading} />
}
